import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import BaseInput from '../../components/form/BaseInput.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import {
    useGetCountriesByContinentQuery,
    useGetGlobalAdvancedSearchFiltersQuery,
    useLazyGlobalAdvancedSearchQuery
} from '../../api/global-view.js';
import AdvancedSearchFilterGlobal from '../../components/misc/AdvancedSearchFilterGlobal';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import circle from '../../resources/images/circle.png';
import { renderFieldWithHighlighting } from '../../utils/highlight.js';
import { useSelector } from 'react-redux';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import IconDownload from '../../components/images/IconDownload.jsx';
import IconEditCompany from '../../components/images/IconEditCompany.jsx';
import IconMP3 from '../../components/images/IconMP3.jsx';
import IconPlay from '../../components/images/IconPlay.jsx';
import IconDocument from '../../components/images/IconDocument.jsx';
import IconSpreadsheet from '../../components/images/IconSpreadsheet.jsx';
import { downloadFile } from '../../utils/file.js';
import FilePreview from '../../components/misc/FilePreview.jsx';
import { PERMISSION_FILES } from '../../utils/constants.js';

const mapLocations = (locationsArr) =>
    Array.isArray(locationsArr) ? locationsArr.map((loc) => ({ label: loc, value: loc })) : [];

const buildCountriesTree = (data) => {
    if (!data || !data.response || !data.response.continents) return [];
    const continentsData = data.response.continents;
    return Object.keys(continentsData).map((continent) => ({
        key: `continent:${continent.toLowerCase()}`,
        id: `continent:${continent.toLowerCase()}`,
        label: continent,
        selectable: true,
        isContainer: true,
        children: continentsData[continent].map((country) => ({
            key: `country:${country.id}`,
            id: country.id,
            label: country.name
        }))
    }));
};

const treeElement = (key, item) => {
    return {
        key: key.toString(),
        label: item.name,
        data: item.id,
        ...(item.sub_skills && item.sub_skills.length > 0
            ? { children: item.sub_skills.map((sub, index) => treeElement(`${key}-${index}`, sub)) }
            : {})
    };
};

const getActivePrefilter = (filters, locationState) => {
    const { preFilterCountry, preFilterContinent } = locationState || {};
    if (preFilterCountry) {
        if (filters.countries && filters.countries.length === 1) {
            return preFilterCountry;
        }
    } else if (preFilterContinent) {
        if (filters.countries && filters.countries.length > 0) {
            return preFilterContinent;
        }
    }
    return null;
};

const AdvancedSearchGlobal = () => {
    const location = useLocation();
    const organizationSwitch = useSelector((state) => state.organizationSwitch);
    const { user, permissions: userPermissions } = useCurrentUser();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        founded: '',
        industry: '',
        ownership: '',
        date: '',
        locations: '',
        countries: [],
        employees: { min: '', max: '' },
        revenue: { min: '', max: '' },
        profit: { min: '', max: '' },
        expenses: { min: '', max: '' },
        needs: [],
        capabilities: [],
        companies: [],
        contacts: [],
        political_affiliations: [],
        ranking: '',
        budget: { min: '', max: '' },
        actual_costs: { min: '', max: '' },
        type: '',
        start_date: '',
        end_date: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [applied, setApplied] = useState(false);
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCountries, setSelectedCountries] = useState({});
    const [downloading, setDownloading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [visible, setVisible] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const { data: filtersAdv } = useGetGlobalAdvancedSearchFiltersQuery();
    const filtersList = useMemo(() => filtersAdv?.response || {}, [filtersAdv]);

    const { data: countriesbyContinentsData } = useGetCountriesByContinentQuery();
    const unifiedCountriesTree = useMemo(
        () => buildCountriesTree(countriesbyContinentsData),
        [countriesbyContinentsData]
    );

    const flatCountries = useMemo(() => {
        const flatten = (nodes) =>
            nodes.reduce((acc, node) => {
                if (node.children && node.children.length > 0) {
                    return acc.concat(flatten(node.children));
                }
                return acc.concat({ name: node.label, id: node.id });
            }, []);
        return flatten(unifiedCountriesTree);
    }, [unifiedCountriesTree]);

    const optionsMap = useMemo(
        () => ({
            status: filtersList.project_statuses || [],
            ownership: filtersList.ownership_types || [],
            type: filtersList.file_types || [],
            locations: mapLocations(filtersList.locations),
            countries: flatCountries || [],
            companies: filtersList.contact_companies || [],
            political_affiliations: filtersList.political_affiliations || []
        }),
        [filtersList, flatCountries]
    );

    const treeOptions = useMemo(() => {
        return filtersList.skills ? filtersList.skills.map((item, index) => treeElement(index, item)) : [];
    }, [filtersList]);

    const countValid = useMemo(() => {
        return Object.values(filters).reduce((count, value) => {
            if (Array.isArray(value)) return value.length > 0 ? count + 1 : count;
            if (typeof value === 'object' && value !== null) {
                return value.min || value.max ? count + 1 : count;
            }
            return (value || value === 0) && value !== '' ? count + 1 : count;
        }, 0);
    }, [filters]);

    const [advancedSearch] = useLazyGlobalAdvancedSearchQuery();
    const debouncedSearch = useDebounce(search, 500);

    const resetSearchAndFilters = () => {
        setSearch('');
        setFilters({
            status: '',
            founded: '',
            industry: '',
            ownership: '',
            date: '',
            locations: '',
            countries: [],
            employees: { min: '', max: '' },
            revenue: { min: '', max: '' },
            profit: { min: '', max: '' },
            expenses: { min: '', max: '' },
            needs: [],
            capabilities: [],
            companies: [],
            contacts: [],
            political_affiliations: [],
            ranking: '',
            budget: { min: '', max: '' },
            actual_costs: { min: '', max: '' },
            type: '',
            start_date: '',
            end_date: ''
        });
        setSelectedCountries({});
        setResults([]);
    };

    const buildRequestParams = useCallback(() => {
        const privateOrg = organizationSwitch[user?.id]?.private || null;
        const selectedOrganizations = organizationSwitch[user?.id]?.organizations
            ? Object.keys(organizationSwitch[user?.id].organizations).filter(
                  (key) => organizationSwitch[user?.id].organizations[key]
              )
            : [];

        return {
            search,
            status: filters.status,
            founded: filters.founded,
            industry: filters.industry,
            ownership: filters.ownership,
            date: filters.date,
            locations: filters.locations,
            countries: filters.countries.filter((id) => typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id)).join(','),
            employees:
                filters.employees.min || filters.employees.max
                    ? `${filters.employees.min || -1},${filters.employees.max || -1}`
                    : '',
            revenue:
                filters.revenue.min || filters.revenue.max
                    ? `${filters.revenue.min || -1},${filters.revenue.max || -1}`
                    : '',
            profit:
                filters.profit.min || filters.profit.max
                    ? `${filters.profit.min || -1},${filters.profit.max || -1}`
                    : '',
            expenses:
                filters.expenses.min || filters.expenses.max
                    ? `${filters.expenses.min || -1},${filters.expenses.max || -1}`
                    : '',
            needs: filters.needs.join(','),
            capabilities: filters.capabilities.join(','),
            companies: filters.companies.map((c) => c.id).join(','),
            contacts: Array.isArray(filters.contacts) ? filters.contacts.map((c) => c.contact_id).join(',') : '',
            political_affiliations: filters.political_affiliations.join(','),
            ranking: filters.ranking ? filters.ranking : '',
            budget:
                filters.budget.min || filters.budget.max
                    ? `${filters.budget.min || -1},${filters.budget.max || -1}`
                    : '',
            actual_costs:
                filters.actual_costs.min || filters.actual_costs.max
                    ? `${filters.actual_costs.min || -1},${filters.actual_costs.max || -1}`
                    : '',
            type: filters.type,
            start_date: filters.start_date,
            end_date: filters.end_date,
            ...(selectedOrganizations.length > 0 && { organizations: selectedOrganizations.join(',') }),
            ...(privateOrg !== null && { private: privateOrg })
        };
    }, [organizationSwitch, user, filters, search]);

    const applyFiltersCallback = useCallback(async () => {
        const params = buildRequestParams();
        setIsLoading(true);
        const result = await advancedSearch(params);
        if (result?.data?.response) {
            setResults(result.data.response);
        }
        setIsLoading(false);
        setHasSearched(true);
    }, [advancedSearch, buildRequestParams]);
    const initializedRef = useRef(false);
    useEffect(() => {
        const { preFilterContinent, preFilterCountry } = location.state || {};
        if (location.state && !initializedRef.current && flatCountries.length > 0) {
            let newFilters = { ...filters };
            const newSelected = {};
            if (preFilterContinent) {
                const continentNode = unifiedCountriesTree.find(
                    (node) => node.label.toLowerCase() === preFilterContinent.toLowerCase()
                );
                if (continentNode && continentNode.children) {
                    newFilters.countries = continentNode.children.map((child) => child.id);
                    newSelected[continentNode.key] = { checked: true };
                    continentNode.children.forEach((child) => {
                        newSelected[`country:${child.id}`] = { checked: true };
                    });
                }
            }
            if (preFilterCountry) {
                const matchedCountry = flatCountries.find(
                    (country) => country.name.toLowerCase() === preFilterCountry.toLowerCase()
                );
                if (matchedCountry) {
                    newFilters.countries = [matchedCountry.id];
                    newSelected[`country:${matchedCountry.id}`] = { checked: true };

                    const parentContainer = unifiedCountriesTree.find(
                        (container) =>
                            container.children && container.children.some((child) => child.id === matchedCountry.id)
                    );
                    if (parentContainer) {
                        newSelected[parentContainer.key] = { checked: false, partialChecked: true };
                    }
                }
            }

            setFilters(newFilters);
            setSelectedCountries(newSelected);
            setApplied(true);
            initializedRef.current = true;
        }
    }, [location.state, unifiedCountriesTree, flatCountries, filters, setFilters]);

    const lastRequestRef = useRef('');
    useEffect(() => {
        const currentRequest = JSON.stringify({ search, filters });
        if (
            ((debouncedSearch !== '' && debouncedSearch === search) || (countValid > 0 && applied)) &&
            currentRequest !== lastRequestRef.current
        ) {
            lastRequestRef.current = currentRequest;
            applyFiltersCallback();
            setApplied(false);
        }
    }, [applyFiltersCallback, debouncedSearch, search, countValid, applied, filters]);

    const handleDownload = (file) => {
        downloadFile(file.url, file.title, file.extension, setDownloading);
    };

    const handlePreview = (file) => {
        setSelectedFile(file);
        setVisible(true);
    };

    const renderFileDisplay = (file) => {
        const { category, url } = file;

        if (category === 'media') {
            if (url.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-white rounded-t-2xl shadow-md'>
                        <img src={url} alt='Media File' className='h-full w-full object-cover rounded-t-2xl' />
                    </div>
                );
            } else if (url.match(/\.(mp3|wav)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                        <IconMP3 className='w-11 h-11 fill-theme1-primary' />
                    </div>
                );
            } else if (url.match(/\.(mp4|avi)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                        <IconPlay className='w-11 h-11 stroke-theme1-primary' />
                    </div>
                );
            }
        } else if (category === 'document') {
            return (
                <div className='h-[107px] bg-theme1-tan bg-opacity-[85%] rounded-t-2xl flex items-center justify-center'>
                    <IconDocument className='w-11 h-11 stroke-theme1-primary' />
                </div>
            );
        } else if (category === 'spreadsheet') {
            return (
                <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                    <IconSpreadsheet className='w-11 h-11 stroke-theme1-primary' />
                </div>
            );
        }
    };

    const isResultsEmpty = () => {
        if (Array.isArray(results)) {
            return results.length === 0;
        }
        return !results || Object.keys(results).length === 0;
    };

    const getNoResultsMessage = () => {
        if (search === '' && isResultsEmpty()) {
            return 'Search and/or filter to display global results!';
        }

        const { preFilterContinent, preFilterCountry } = location.state || {};
        if (preFilterCountry) {
            return `No results found for ${preFilterCountry}!`;
        } else if (preFilterContinent) {
            return `No results found for ${preFilterContinent}!`;
        } else {
            return 'No results found!';
        }
    };

    const renderResults = () => {
        const { meetings, contacts, organizations, projects, files } = results || {};

        const meetingsArray = meetings ? (Array.isArray(meetings) ? meetings : Object.values(meetings).flat()) : [];

        const hasResults =
            (organizations && organizations.length > 0) ||
            (meetings && meetings.length > 0) ||
            (contacts && contacts.length > 0) ||
            (projects && projects.length > 0) ||
            (files && files.length > 0);

        const activePreFilter = getActivePrefilter(filters, location.state);

        return (
            <div className='max-w-3xl w-full mx-auto px-4'>
                {activePreFilter && hasResults && (
                    <div className='mb-6 text-xl  text-theme1-text1'>
                        Results found for <span className='font-bold'>{activePreFilter}</span>
                    </div>
                )}

                {organizations && organizations.length > 0 && (
                    <div className='mb-6'>
                        <BaseCard className='p-6 bg-white shadow-lg rounded-4xl border-0'>
                            <h2 className='text-theme1-text1 font-bold text-2xl mb-4'>Organizations</h2>
                            <p className='text-theme1-text2 mb-4'>
                                {search !== ''
                                    ? `${organizations.length} organization(s) found matching "${search}"`
                                    : `Found ${organizations.length} organization(s)`}
                            </p>
                            {organizations.map((org) => (
                                <div key={org.id} className='border-t border-theme1-grey py-4'>
                                    <Link
                                        to={`/organizations/${org.id}`}
                                        className='text-lg text-theme1-success font-bold'
                                    >
                                        {renderFieldWithHighlighting(org.name, 'domain', org.matched_fields)}
                                    </Link>
                                </div>
                            ))}
                        </BaseCard>
                    </div>
                )}

                {meetingsArray && meetingsArray.length > 0 && (
                    <div className='mb-6'>
                        <BaseCard className='p-6 bg-white shadow-lg rounded-4xl border-0'>
                            <h2 className='text-theme1-text1 font-bold text-2xl mb-4'>Meetings</h2>
                            <p className='text-theme1-text2 mb-4'>
                                {search !== ''
                                    ? `${meetingsArray.length} meeting(s) found matching "${search}"`
                                    : `Found ${meetingsArray.length} meeting(s)`}
                            </p>
                            {meetingsArray.map((meeting, index) => (
                                <div key={index} className='border-t border-theme1-grey py-4'>
                                    <div className='text-lg text-theme1-success font-bold'>
                                        {renderFieldWithHighlighting(meeting.title, 'title', meeting.matched_fields)}
                                    </div>
                                </div>
                            ))}
                        </BaseCard>
                    </div>
                )}

                {contacts && contacts.length > 0 && (
                    <div className='mb-6'>
                        <BaseCard className='p-6 bg-white shadow-lg rounded-4xl border-0'>
                            <h2 className='text-theme1-text1 font-bold text-2xl mb-4'>Contacts</h2>
                            <p className='text-theme1-text2 mb-4'>
                                {search !== ''
                                    ? `${contacts.length} contact(s) found matching "${search}"`
                                    : `Found ${contacts.length} contact(s)`}
                            </p>
                            {contacts.map((contact) => (
                                <div key={contact.id} className='border-t border-theme1-grey py-4'>
                                    <Link
                                        to={`/contacts/view/${contact.id}`}
                                        className='text-xl text-theme1-success font-bold'
                                    >
                                        {renderFieldWithHighlighting(
                                            contact.first_name,
                                            'first_name',
                                            contact.matched_fields
                                        )}{' '}
                                        {contact.middle_name
                                            ? renderFieldWithHighlighting(
                                                  contact.middle_name,
                                                  'middle_name',
                                                  contact.matched_fields
                                              )
                                            : ''}{' '}
                                        {contact.last_name
                                            ? renderFieldWithHighlighting(
                                                  contact.last_name,
                                                  'last_name',
                                                  contact.matched_fields
                                              )
                                            : ''}
                                    </Link>
                                </div>
                            ))}
                        </BaseCard>
                    </div>
                )}

                {projects && projects.length > 0 && (
                    <div className='mb-6'>
                        <BaseCard className='p-6 bg-white shadow-lg rounded-4xl border-0'>
                            <h2 className='text-theme1-text1 font-bold text-2xl mb-4'>Projects</h2>
                            <p className='text-theme1-text2 mb-4'>
                                {search !== ''
                                    ? `${projects.length} project(s) found matching "${search}"`
                                    : `Found ${projects.length} project(s)`}
                            </p>
                            {projects.map((project) => (
                                <div key={project.id} className='border-t border-theme1-grey py-4'>
                                    <Link
                                        to={`/projects/${project.id}`}
                                        className='text-lg text-theme1-success font-bold'
                                    >
                                        {renderFieldWithHighlighting(project.name, 'name', project.matched_fields)}
                                    </Link>
                                </div>
                            ))}
                        </BaseCard>
                    </div>
                )}

                {files && files.length > 0 && (
                    <div className='mb-6'>
                        <BaseCard className='p-6 bg-white shadow-lg rounded-4xl border-0'>
                            <h2 className='text-theme1-text1 font-bold text-2xl mb-4'>Files</h2>
                            <p className='text-theme1-text2 mb-4'>
                                {search !== ''
                                    ? `${files.length} file(s) found matching "${search}"`
                                    : `Found ${files.length} file(s)`}
                            </p>
                            <div className='grid grid-cols-3 sm:grid-cols-4 gap-4'>
                                {files.map((file) => (
                                    <div
                                        key={file.id}
                                        className='border border-theme1-cardStroke bg-theme1-white rounded-2xl text-theme1-text1 flex flex-col relative'
                                    >
                                        <div onClick={() => handlePreview(file)}>{renderFileDisplay(file)}</div>
                                        <div className='p-4'>
                                            <h3 className='text-sm font-bold truncate'>
                                                {renderFieldWithHighlighting(file.title, 'title', file.matched_fields)}
                                            </h3>
                                            {file.description && (
                                                <p className='text-sm text-theme1-text2 truncate'>
                                                    {renderFieldWithHighlighting(
                                                        file.description,
                                                        'description',
                                                        file.matched_fields
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                        <div className='flex justify-between p-4'>
                                            <button
                                                type='button'
                                                onClick={() => handleDownload(file)}
                                                className='cursor-pointer'
                                                disabled={downloading}
                                            >
                                                <IconDownload className='w-8 h-8 fill-theme1-text3' />
                                            </button>
                                            {!!userPermissions[PERMISSION_FILES].update && (
                                                <button
                                                    onClick={() => navigate(`/files/edit/${file.id}`)}
                                                    className='cursor-pointer'
                                                >
                                                    <IconEditCompany className='w-9 h-9 fill-theme1-text3' />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </BaseCard>
                    </div>
                )}

                {!hasResults &&
                    (location.state?.preFilterCountry || location.state?.preFilterContinent
                        ? hasSearched
                        : hasSearched || search === '') && (
                        <div className='py-12 flex flex-col justify-center items-center'>
                            <div className='text-2xl text-theme1-primary2 font-light max-w-80 text-center'>
                                {getNoResultsMessage()}
                            </div>
                            <img alt='No results' src={circle} className='my-8 w-40 h-40' />
                        </div>
                    )}
            </div>
        );
    };

    return (
        <AuthLayout title='Advanced Search' backRoute='/global-view'>
            <BaseCard className='border-0 m-0 mb-6 -mt-6 p-4 pt-10 rounded-t-none bg-theme1-white shadow-lg max-w-3xl w-full mx-auto'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-between items-center mt-6 mb-4'>
                        <div className='flex-grow'>
                            <BaseInput
                                name='search'
                                type='text'
                                value={search}
                                placeholder='Search... (min 3 characters)'
                                setValue={setSearch}
                            />
                        </div>
                    </div>
                </div>
                <div className='mb-6 px-4 text-right'>
                    <div className='flex items-center w-full text-theme1-primary2'>
                        <div className='flex items-center space-x-3'>
                            <div className='flex items-center space-x-1'>
                                <BaseButton
                                    className='flex bg-theme1-lightBtns text-theme1-white p-3 pb-[10px]'
                                    onClick={() => setShowFilters(true)}
                                >
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='23'
                                        height='23'
                                        viewBox='0 0 20 20'
                                        fill='none'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M4.09254 3.2116C4.17673 3.18208 4.26531 3.16699 4.35454 3.16699H14.6462C14.7354 3.16699 14.824 3.18208 14.9082 3.2116C15.1362 3.29158 15.3429 3.42268 15.5125 3.59487C15.682 3.76706 15.8099 3.97578 15.8863 4.20502C15.9627 4.43426 15.9856 4.67795 15.9533 4.91743C15.921 5.15691 15.8343 5.38581 15.6999 5.5866C15.6782 5.61903 15.6541 5.64981 15.6278 5.67869L11.8754 9.80639V15.042C11.8754 15.3419 11.706 15.616 11.4377 15.7501C11.1695 15.8842 10.8486 15.8552 10.6087 15.6753L7.44204 13.3003C7.24269 13.1508 7.12537 12.9162 7.12537 12.667V9.80639L3.37292 5.67869C3.34666 5.64981 3.32258 5.61903 3.30087 5.5866C3.16643 5.38581 3.07973 5.15691 3.04742 4.91743C3.01511 4.67796 3.03804 4.43427 3.11445 4.20502C3.19087 3.97578 3.31874 3.76706 3.48828 3.59487C3.65781 3.42268 3.86451 3.29157 4.09254 3.2116ZM4.66876 4.75033L8.50282 8.96779C8.6353 9.11352 8.7087 9.30339 8.7087 9.50033V12.2712L10.292 13.4587V9.50033C10.292 9.30339 10.3654 9.11352 10.4979 8.96779L14.332 4.75033H4.66876Z'
                                            fill='white'
                                        />
                                    </svg>
                                    <span className='text-sm text-theme1-white px-2'>
                                        {countValid} filter(s) applied
                                    </span>
                                </BaseButton>
                            </div>
                        </div>
                        <BaseButton
                            className='ml-auto w-min rounded-4xl border font-normal btn-red-outline px-4 py-3'
                            onClick={resetSearchAndFilters}
                        >
                            Reset
                        </BaseButton>
                    </div>
                </div>

                <AdvancedSearchFilterGlobal
                    filters={filters}
                    setFilters={setFilters}
                    visible={showFilters}
                    setVisible={setShowFilters}
                    applyFilters={applyFiltersCallback}
                    setApplied={setApplied}
                    optionsMap={optionsMap}
                    treeOptions={treeOptions}
                    selectedCountries={selectedCountries}
                    setSelectedCountries={setSelectedCountries}
                    countriesTree={unifiedCountriesTree}
                />
            </BaseCard>

            {isLoading ? <ThreeDotsLoader /> : renderResults()}

            {selectedFile && (
                <FilePreview
                    file={selectedFile}
                    visible={visible}
                    setVisible={setVisible}
                    setSelectedFile={setSelectedFile}
                    search={search}
                />
            )}
        </AuthLayout>
    );
};

export default AdvancedSearchGlobal;
